import { useState, useCallback, useMemo, useEffect } from "react";
import { isEqual } from "lodash";
import { Icon, Label } from "semantic-ui-react";
import FilterSidebar from "../filterSidebar/FilterSidebar";
import { calculateAppliedFilters, type FiltersMap } from "../../utils/filterUtils";
import { FilterFormBuilder, type FilterItemBase } from "../filterForms/FilterFormBuilder/FilterFormBuilder";
import { FilterViewTypes } from "enums/filterViewTypes";
import { AddOnIds } from "enums";
import {
  type Accounts,
  type PacksListItem,
  allCustomerAccounts,
  customDropdownSearch,
  dateRange30,
  longFormDateFilter,
  mappedPriorityLevels,
  mappedStatusLevels,
  type PerformanceFilter,
  validDateWithMoment,
  PackStatusFilter,
} from "features/Library/Common/utils/performanceUtils";
import moment from "moment";
import { ReportingChip } from "components/reportingChip/ReportingChip";
import { type AccountType } from "../../interfaces/Account";

import "./ReportingFilter.scss";
import { type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { Tooltip } from "components/common/tooltip";
import { useAddOnQuery } from "features/Reporting/Content/queries/useAddOnQuery";
import { useReportingAccountsList, useReportingAccountType, useReportingPacksList } from "./ReportingFilterRequests";
import { formatActiveFilter, extractAndValidateDates, buildFilterCallback } from "./ReportingFilterUtils";
import type { AddOn } from "features/Accounts/types";

const { dateFrom, dateTo } = dateRange30();

export interface FlowFilterItem {
  id: number;
  title: string;
}

enum PackTypeId {
  All = 0,
  Free = 1,
  Paid = 2,
  Other = 3,
  Trial = 4,
}

const addOnsIsActiveMap = {
  0: { text: "All Statuses", value: null },
  1: { text: "Active", value: true },
  2: { text: "Expired", value: false },
} as const;

const placeholderFlowId = { text: "All", value: 0 };

type FilterWithoutAccounts = {
  currentFilter: BaseFilter;
  callback: (filter: BaseFilter) => void;
  supportsSPType?: boolean;
  initialDateRange?: PerformanceFilter;
  ignoreInCountAndHide?: string[];
  includeAccounts?: never;
  publisherId?: number;
  includeAccountsDropdown?: never;
  includePacks?: never;
  includeDistinct?: boolean;
  includeVideoType?: boolean;
  includePriority?: boolean;
  includeStatus?: boolean;
  includeAddons?: boolean;
  flowsData?: FlowFilterItem[];
  overrideAccountId?: number;
  initialOverrides?: never;
  accountsLabel?: string;
};

type FilterWithAccounts = {
  currentFilter: AccountFilter;
  callback: (filter: AccountFilter) => void;
  initialDateRange?: PerformanceFilter;
  ignoreInCountAndHide?: string[];
  includeAccounts: boolean;
  publisherId?: number;
  supportsSPType?: boolean;
  includeAccountsDropdown?: boolean;
  includePacks?: never;
  includeDistinct?: boolean;
  includeVideoType?: boolean;
  includePriority?: boolean;
  includeStatus?: boolean;
  includeAddons?: boolean;
  flowsData?: FlowFilterItem[];
  overrideAccountId?: number;
  initialOverrides?: Partial<AccountDropdownFilter>;
  accountsLabel?: string;
};

type FilterWithAccountsOrPacksDropdown = {
  currentFilter: AccountDropdownFilter;
  callback: (filter: AccountDropdownFilter) => void;
  supportsSPType?: boolean;
  initialDateRange?: PerformanceFilter;
  publisherId?: number;
  ignoreInCountAndHide?: string[];
  includeAccounts?: boolean;
  includeAccountsDropdown: boolean;
  includePacks?: boolean;
  includeDistinct?: boolean;
  includeVideoType?: boolean;
  includePriority?: boolean;
  includeStatus?: boolean;
  includeAddons?: boolean;
  flowsData?: FlowFilterItem[];
  overrideAccountId?: number;
  initialOverrides?: Partial<AccountDropdownFilter>;
  accountsLabel?: string;
};

export type FilterProps = PropsFromRedux &
  (FilterWithoutAccounts | FilterWithAccounts | FilterWithAccountsOrPacksDropdown);

export interface BaseFilter {
  dateFrom: string;
  dateTo: string;
  isDistinct?: boolean;
}

export interface AccountFilter extends BaseFilter {
  showCustomers?: boolean;
}

export interface AccountDropdownFilter extends BaseFilter {
  accounts?: Accounts[] | number[] | string[];
  packId?: PacksListItem[];
  includeMyData?: boolean;
  type?: AccountType;
  videoType?: VideoTypes;
  priority?: number[];
  status?: number[];
  packStatusId?: PackStatusFilter;
  packTypeId?: PackTypeId;
  addOnId?: number[];
  isActive?: boolean | null;
  flowId?: number;
}

const filterOutHidden = (filter: any, ignoreInCountAndHide: string[] = []) =>
  Object.fromEntries(Object.entries(filter).filter(([key]) => !ignoreInCountAndHide.includes(key)));

function filtersAreIdentical(newFilter: DateFilterCallback, oldFilter: DateFilterCallback): boolean;
function filtersAreIdentical(newFilter: AccountFilterCallback, oldFilter: AccountFilterCallback): boolean;
function filtersAreIdentical(
  newFilter: AccountDropdownFilterCallback,
  oldFilter: AccountDropdownFilterCallback,
): boolean;

function filtersAreIdentical(newFilter: SubmitCallback, oldFilter: SubmitCallback): boolean {
  return isEqual(newFilter, oldFilter);
}

function valueOrFallback(value: any, fallback: any) {
  return value ?? fallback;
}

/**
 * Extracts the value from an item that could be an object with a value property or a primitive
 */
function extractValue<T extends { value: V }, V>(item: T | V): V {
  if (typeof item === "object" && item !== null && "value" in item) {
    return item.value;
  }
  return item;
}

/**
 * Adds missing items from source arrays to the target Map
 */
function addMissingItems<T extends { value: V }, V>(
  targetMap: Map<V, T>,
  sourceItems: Array<T | V>,
  fallbackMap?: Map<V, T>,
): void {
  for (const item of sourceItems) {
    const itemValue = extractValue<T, V>(item);

    if (targetMap.has(itemValue)) {
      continue;
    }

    const fullItem = typeof item === "object" && item !== null && "value" in item ? item : fallbackMap?.get(itemValue);

    if (fullItem) {
      targetMap.set(itemValue, fullItem);
    }
  }
}

/**
 * Merges items from multiple sources, preserving previously selected items
 * that may not be in the current available list
 */
function mergeFilterItems<T extends { value: V }, V>(
  availableItems: T[],
  currentlyAppliedItems: Array<T | V>,
  inProgressItems: Array<T | V>,
): T[] {
  const itemsMap = new Map<V, T>(availableItems.map((item) => [item.value, item]));

  const inProgressMap = new Map<V, T>();
  for (const item of inProgressItems) {
    if (typeof item === "object" && item !== null && "value" in item) {
      inProgressMap.set(item.value, item);
    }
  }

  addMissingItems(itemsMap, currentlyAppliedItems, inProgressMap);

  return Array.from(itemsMap.values());
}

type FilterOptionsConfig = {
  includeAccounts: boolean | undefined;
  includeAccountsDropdown: boolean | undefined;
  includePacks: boolean | undefined;
  includeDistinct: boolean | undefined;
  includeVideoType: boolean | undefined;
  includePriority: boolean | undefined;
  includeStatus: boolean | undefined;
  includeAddons: boolean | undefined;
  includeFlows: boolean | undefined;
};

const createStateUpdater = (config: FilterOptionsConfig, defaultType: string | undefined) => {
  const updaters: Array<(data: CombinedFilterCallback, filter: SubmitCallback) => CombinedFilterCallback> = [];

  if (config.includeAccounts) {
    updaters.push((data, filter) => ({
      ...data,
      showCustomers: (filter as any).accounts,
    }));
  }

  if (config.includeAccountsDropdown) {
    updaters.push((data, filter) => ({
      ...data,
      accounts: valueOrFallback((filter as any).accounts, []),
      includeMyData: valueOrFallback((filter as any).includeMyData, false),
      type: valueOrFallback((filter as any).type, defaultType),
    }));
  }

  if (config.includePacks) {
    updaters.push((data, filter) => ({
      ...data,
      packId: valueOrFallback((filter as any).packId, []),
      packStatusId: valueOrFallback((filter as any).packStatusId, PackStatusFilter.All),
      packTypeId: valueOrFallback((filter as any).packTypeId, PackTypeId.All),
    }));
  }

  if (config.includeDistinct) {
    updaters.push((data, filter) => ({
      ...data,
      distinct: valueOrFallback((filter as any).distinct, "Distinct"),
    }));
  }

  if (config.includeVideoType) {
    updaters.push((data, filter) => ({
      ...data,
      videoType: valueOrFallback((filter as any).videoType, "All"),
    }));
  }

  if (config.includePriority) {
    updaters.push((data, filter) => ({
      ...data,
      priority: valueOrFallback((filter as any).priority, []),
    }));
  }

  if (config.includeStatus) {
    updaters.push((data, filter) => ({
      ...data,
      status: valueOrFallback((filter as any).status, []),
    }));
  }

  if (config.includeAddons) {
    updaters.push((data, filter) => ({
      ...data,
      addOnId: valueOrFallback((filter as any).addOnId, []),
      isActive: (filter as any).isActive
        ? addOnsIsActiveMap[(filter as any).isActive as keyof typeof addOnsIsActiveMap].value
        : null,
    }));
  }

  if (config.includeFlows) {
    updaters.push((data, filter) => ({
      ...data,
      flowId: valueOrFallback((filter as any).flowId, undefined),
    }));
  }

  return (baseData: DateFilterCallback, filter: SubmitCallback) =>
    updaters.reduce((acc, updater) => updater(acc, filter), baseData as CombinedFilterCallback);
};

const incomingDateFormat: moment.MomentFormatSpecification = "MM/DD/YYYY";

type VideoTypes = "All" | "Video" | "EmbeddedVideo";

type DateFilterCallback = {
  dates: { from: string; to: string };
  distinct?: "Distinct" | "Total";
  videoType?: VideoTypes;
  priority?: number[];
  status?: number[];
  packStatusId?: PackStatusFilter;
  packTypeId?: PackTypeId;
  isActive?: boolean | null | number;
  flowId?: number;
};

type AccountFilterCallback = DateFilterCallback & {
  showCustomers?: boolean;
};

type AccountDropdownFilterCallback = DateFilterCallback & {
  accounts?: Accounts[] | number[] | string[];
  includeMyData?: boolean;
  type?: AccountType;
};

type PacksDropdownFilterCallback = DateFilterCallback & {
  packId?: PacksListItem[];
};

type AddOnDropdownFilterCallback = DateFilterCallback & {
  addOnId?: number[];
};

type FlowDropdownFilterCallback = DateFilterCallback & {
  flowId?: number;
};

type CombinedFilterCallback = AccountFilterCallback &
  AccountDropdownFilterCallback &
  PacksDropdownFilterCallback &
  AddOnDropdownFilterCallback &
  FlowDropdownFilterCallback;

type SubmitCallback =
  | DateFilterCallback
  | AccountFilterCallback
  | AccountDropdownFilterCallback
  | PacksDropdownFilterCallback
  | AddOnDropdownFilterCallback
  | FlowDropdownFilterCallback;

export const ReportingFilter = ({
  currentFilter,
  callback,
  includeAccounts,
  includeAccountsDropdown,
  includePacks,
  includeVideoType,
  includeDistinct,
  includePriority,
  includeStatus,
  includeAddons,
  flowsData = [],
  accountId,
  ignoreInCountAndHide = ["type"],
  initialDateRange,
  accountName,
  supportsSPType,
  publisherId,
  overrideAccountId,
  initialOverrides,
  accountsLabel,
}: FilterProps) => {
  // Derive includeFlows from whether flowsData is provided
  const includeFlows = flowsData?.length > 0;

  const type = useReportingAccountType(accountId, supportsSPType, publisherId);
  const addOnQuery = useAddOnQuery(accountId);
  const shouldSeeDropdown = useMemo(() => {
    if (!supportsSPType) return false;
    const addOns = addOnQuery.data ?? [];
    const hasManageAccountsAddOn = addOns.some((addon) => addon.id === AddOnIds.CreateAccounts && addon.isEnabled);
    const hasMarketplaceAddOn = addOns.some((addon) => addon.id === AddOnIds.Marketplace && addon.isEnabled);
    return hasManageAccountsAddOn && hasMarketplaceAddOn && type !== "bsi";
  }, [addOnQuery.data, supportsSPType, type]);

  const parsedIgnoreInCountAndHide = useMemo(() => {
    if (shouldSeeDropdown) {
      return ignoreInCountAndHide?.filter((i) => i !== "type");
    }
    return ignoreInCountAndHide;
  }, [ignoreInCountAndHide, shouldSeeDropdown]);

  const initialFilter = useMemo<SubmitCallback>(() => {
    const baseFilter: any = {
      dates: { from: initialDateRange?.dateFrom ?? dateFrom, to: initialDateRange?.dateTo ?? dateTo },
      ...(includeAccounts && { showCustomers: false }),
      ...(includeAccountsDropdown && { accounts: [allCustomerAccounts.value] }),
      ...(includeAccountsDropdown && { includeMyData: true }),
      ...(includeAccountsDropdown && { type }),
      ...(includePacks && { packId: [] }),
      ...(includePacks && { packStatusId: PackStatusFilter.All }),
      ...(includePacks && { packTypeId: PackTypeId.All }),
      ...(includeDistinct && { distinct: "Total" }),
      ...(includeVideoType && { videoType: "All" }),
      ...(includePriority && { priority: [] }),
      ...(includeStatus && { status: [] }),
      ...(includeAddons && { addOnId: [], isActive: 0 }),
      ...(includeFlows && { flowId: 0 }),
    };

    // Only apply overrides for properties that already exist in baseFilter
    if (initialOverrides) {
      const filteredOverrides = Object.fromEntries(
        Object.entries(initialOverrides).filter(([key]) => key in baseFilter),
      );
      return { ...baseFilter, ...filteredOverrides };
    }
    return baseFilter;
  }, [
    includeAccounts,
    includeAccountsDropdown,
    includePacks,
    includeDistinct,
    type,
    initialDateRange,
    includeVideoType,
    includePriority,
    includeStatus,
    includeAddons,
    includeFlows,
    initialOverrides,
  ]);

  const [show, setShow] = useState(false);
  const [selectedData, setSelectedData] = useState<SubmitCallback>(initialFilter);
  const currentAccountType = includeAccountsDropdown
    ? (selectedData as unknown as AccountDropdownFilter).type ?? type
    : "account";

  const [accountsSearchTerm, setAccountsSearchTerm] = useState("");
  const reportingAccountsWithSearch = useReportingAccountsList(currentAccountType ?? "account", accountId, {
    top: 100,
    term: accountsSearchTerm,
    accumulate: true,
  });

  const currentFilterAccounts = useMemo<Accounts[] | number[] | string[] | undefined>(() => {
    if (includeAccountsDropdown) {
      if (overrideAccountId) {
        return [overrideAccountId];
      } else {
        const accounts = (selectedData as unknown as AccountDropdownFilter).accounts ?? [];
        return accounts.map((account) => {
          // Extract the value from account objects, or return primitives as-is
          if (typeof account === "object") {
            return account.value;
          }
          return account;
        }) as Accounts[] | number[] | string[];
      }
    } else {
      return undefined;
    }
  }, [includeAccountsDropdown, overrideAccountId, selectedData]);

  const currentFilterIncludeMyData = useMemo(() => {
    if (includeAccountsDropdown) {
      if (overrideAccountId) {
        return false;
      } else {
        return (selectedData as unknown as AccountDropdownFilter).includeMyData ?? false;
      }
    } else {
      return undefined;
    }
  }, [includeAccountsDropdown, overrideAccountId, selectedData]);

  const reportingPacksWithSearch = useReportingPacksList(
    currentFilterAccounts,
    currentFilterIncludeMyData,
    currentAccountType,
    includePacks,
  );

  // Merge currently selected accounts with the available accounts so users can always see and manage their picks
  const mergedAccountsList = useMemo(() => {
    if (!includeAccountsDropdown) {
      return reportingAccountsWithSearch.items;
    }

    const currentlyApplied = valueOrFallback((currentFilter as AccountDropdownFilter).accounts, []);
    const inProgress = valueOrFallback((selectedData as AccountDropdownFilterCallback).accounts, []);

    return mergeFilterItems(reportingAccountsWithSearch.items, currentlyApplied, inProgress);
  }, [includeAccountsDropdown, reportingAccountsWithSearch.items, currentFilter, selectedData]);

  // Merge currently selected packs with the available packs to retain previously selected packs
  // even when they're not in the filtered list (e.g., when drilling into an account). Without this, only the ones that are in the new call to the endpoint will display, but when you drill back up, they all appear again.
  const mergedPacksList = useMemo(() => {
    if (!includePacks) {
      return reportingPacksWithSearch.items;
    }

    const currentlyApplied = valueOrFallback(currentFilter.packId, []);
    const inProgress = valueOrFallback((selectedData as PacksDropdownFilterCallback).packId, []);

    return mergeFilterItems(reportingPacksWithSearch.items, currentlyApplied, inProgress);
  }, [includePacks, reportingPacksWithSearch.items, currentFilter, selectedData]);

  // Toggle showCustomers to false if the box was checked before and
  // includeAccounts is set to false
  useEffect(() => {
    if (!includeAccounts && (currentFilter as AccountFilter).showCustomers === true) {
      let copy = { ...currentFilter };
      // @ts-ignore
      callback({ ...copy, showCustomers: false });
    }
    // Disabling for now, may put callback back in later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter, includeAccounts, selectedData]);

  // Changing performance filter style to be styled in the way FilterSidebar expects
  const formattedActiveFilter = useMemo<any>(() => {
    return formatActiveFilter(currentFilter);
  }, [currentFilter]);

  const hide = useCallback(() => setShow(false), []);

  const handleSubmit = useCallback(
    (filter: SubmitCallback) => {
      const dates = extractAndValidateDates(filter, parsedIgnoreInCountAndHide, initialFilter);
      if (!dates) return;

      if (filtersAreIdentical(filter, formattedActiveFilter)) {
        return;
      }

      const updateNewStateData = createStateUpdater(
        {
          includeAccounts,
          includeAccountsDropdown,
          includePacks,
          includeDistinct,
          includeVideoType,
          includePriority,
          includeStatus,
          includeAddons,
          includeFlows,
        },
        type,
      );

      const newStateData: CombinedFilterCallback = updateNewStateData({ dates }, filter);

      // reset any hidden filters to the state they were when they became hidden
      parsedIgnoreInCountAndHide?.forEach(
        (property) => ((newStateData as any)[property] = (currentFilter as any)[property]),
      );

      setSelectedData(newStateData);
      setShow(false);

      const longFormDates = longFormDateFilter(dates.from, dates.to);
      const callbackData = buildFilterCallback(newStateData, filter, longFormDates, {
        includeAccounts,
        includeAccountsDropdown,
        includePacks,
        includeDistinct,
        includeVideoType,
        includePriority,
        includeStatus,
        includeAddons,
        includeFlows,
      });

      callback(callbackData);
    },
    [
      formattedActiveFilter,
      includeAccounts,
      includeAccountsDropdown,
      includePacks,
      includeDistinct,
      callback,
      type,
      parsedIgnoreInCountAndHide,
      currentFilter,
      includeVideoType,
      includePriority,
      includeStatus,
      includeAddons,
      includeFlows,
      initialFilter,
    ],
  );

  const handleReset = useCallback(() => {
    const { dates, distinct, isActive, ...restOfFilter } = initialFilter;
    const filteredResetOfFilter = filterOutHidden(restOfFilter, parsedIgnoreInCountAndHide);
    const keysToRemove = ["dates", "distinct"]; // These keys have different names in the formattedActiveFilter, so we need to remove them in order to not pass them to the callback.
    const formattedActiveFilterSansKeys = Object.fromEntries(
      Object.entries(formattedActiveFilter).filter(([key]) => !keysToRemove.includes(key)),
    );
    let callbackValues: any = {
      dateFrom: dates.from,
      dateTo: dates.to,
      ...formattedActiveFilterSansKeys,
      ...filteredResetOfFilter,
    };

    setSelectedData(initialFilter);

    // Applied filter is empty? No need to run callback
    if (
      Object.keys(formattedActiveFilter).length === 0 ||
      (includeAccountsDropdown && filtersAreIdentical(initialFilter, formattedActiveFilter))
    ) {
      return;
    }

    if (distinct !== undefined) {
      callbackValues.isDistinct = distinct === "Distinct";
    }

    if (isActive !== undefined) {
      switch (isActive) {
        case 0:
          callbackValues.isActive = null;
          break;
        case 1:
          callbackValues.isActive = true;
          break;
        case 2:
          callbackValues.isActive = false;
          break;
      }
    }

    callback(callbackValues);
  }, [formattedActiveFilter, callback, includeAccountsDropdown, initialFilter, parsedIgnoreInCountAndHide]);

  const filterCount = useMemo(() => {
    let filteredFilter: FiltersMap = {};
    for (let filterName in formattedActiveFilter) {
      const value = formattedActiveFilter[filterName];
      // Check for null/undefined instead of truthiness to allow 0 values (e.g., PackTypeId.All)
      const hasValue = value !== null && value !== undefined && value !== "";
      if (!parsedIgnoreInCountAndHide?.includes(filterName) && hasValue) {
        filteredFilter[filterName] = value;
      }
    }

    return calculateAppliedFilters(filteredFilter);
  }, [formattedActiveFilter, parsedIgnoreInCountAndHide]);

  const updateFilter = useCallback((filter: SubmitCallback) => {
    setSelectedData(filter);
  }, []);

  const shouldApplyFilterBeDisabled = useMemo<boolean>(() => {
    if (
      includeAccountsDropdown &&
      !["accounts", "includeMyData"].every((item) => parsedIgnoreInCountAndHide?.includes(item))
    ) {
      const noAccountSelected =
        !(selectedData as AccountDropdownFilterCallback).includeMyData &&
        ((selectedData as any).accounts === undefined || (selectedData as any).accounts.length === 0);

      if (noAccountSelected) {
        return true;
      }
    }

    if (filtersAreIdentical(selectedData, formattedActiveFilter)) return true;

    if (!parsedIgnoreInCountAndHide?.includes("dates")) {
      if (!validDateWithMoment(selectedData.dates?.from) || !validDateWithMoment(selectedData.dates?.to)) return true;
      if (
        moment(selectedData.dates.from, incomingDateFormat).isAfter(moment(selectedData.dates.to, incomingDateFormat))
      )
        return true;
    }
    if (Object.keys(selectedData).length === 0) return true;

    return false;
  }, [selectedData, formattedActiveFilter, includeAccountsDropdown, parsedIgnoreInCountAndHide]);

  const filterChipData = useMemo(() => {
    const chips = [];
    if (!parsedIgnoreInCountAndHide?.includes("dates")) {
      chips.push({
        name: "Date",
        value: `${currentFilter.dateFrom} - ${currentFilter.dateTo}`,
      });
    }
    return chips;
  }, [parsedIgnoreInCountAndHide, currentFilter.dateFrom, currentFilter.dateTo]);

  const filterChips = filterChipData.map((chipData) => (
    <ReportingChip key={chipData.name} name={chipData.name} value={chipData.value} />
  ));

  return (
    <div className="filterRoot">
      <FilterSidebar
        visible={show}
        hideFilter={hide}
        applyFilter={handleSubmit}
        resetFilter={handleReset}
        appliedFilter={filterOutHidden(formattedActiveFilter, parsedIgnoreInCountAndHide)}
        shouldDisableApply={shouldApplyFilterBeDisabled}
        defaultFilter={filterOutHidden(initialFilter, parsedIgnoreInCountAndHide)}
      >
        <Filters
          filter={selectedData}
          accounts={mergedAccountsList}
          packs={mergedPacksList}
          updateSubFilter={updateFilter}
          includeAccounts={includeAccounts}
          includeAccountsDropdown={includeAccountsDropdown}
          includePacks={includePacks}
          shouldSeeDropdown={shouldSeeDropdown}
          includeDistinct={includeDistinct}
          includeVideoType={includeVideoType}
          includeAddons={includeAddons}
          flowsData={flowsData}
          includeFlows={includeFlows}
          accountName={accountName}
          accountsLoadMore={reportingAccountsWithSearch.hasMore ? reportingAccountsWithSearch.loadMore : undefined}
          accountsHasMore={reportingAccountsWithSearch.hasMore}
          accountsLoading={reportingAccountsWithSearch.isLoading || reportingAccountsWithSearch.isFetchingNext}
          onAccountsSearchTermChange={setAccountsSearchTerm}
          packsLoading={reportingPacksWithSearch.isLoading}
          ignoreInCountAndHide={parsedIgnoreInCountAndHide}
          includePriority={includePriority}
          includeStatus={includeStatus}
          addOnsData={addOnQuery.data}
          accountsLabel={accountsLabel}
        />
      </FilterSidebar>
      {filterChips}
      <button
        className="filterVisibilityToggle"
        data-testid="filterVisiblityToggle"
        onClick={() => setShow((s) => !s)}
        type="button"
      >
        <Icon name="filter" />
        Filter
      </button>
      {filterCount > 0 && (
        <Label circular color="blue">
          {filterCount}
        </Label>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
    accountName: state.accounts.accountInformation.accountInfo.accountName,
  };
};

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ReportingFilter);
export default ConnectedComponent;

interface SubFilterProps {
  includeAccounts?: boolean;
  includeAccountsDropdown?: boolean;
  includePacks?: boolean;
  shouldSeeDropdown?: boolean;
  includeDistinct?: boolean;
  includeVideoType?: boolean;
  includePriority?: boolean;
  includeStatus?: boolean;
  includeAddons?: boolean;
  flowsData?: FlowFilterItem[];
  includeFlows?: boolean;
  filter: any;
  updateSubFilter: (filter: SubmitCallback) => void;
  updateFilter?: any;
  accounts: Accounts[];
  packs: PacksListItem[];
  accountName?: string;
  ignoreInCountAndHide?: string[];
  onAccountsSearchTermChange?: (term: string) => void;
  accountsLoadMore?: () => void;
  accountsHasMore?: boolean;
  accountsLoading?: boolean;
  packsLoading?: boolean;
  addOnsData?: AddOn[];
  accountsLabel?: string;
}

const Filters = ({
  includeAccounts,
  includeAccountsDropdown,
  includePacks,
  includeDistinct,
  includeVideoType,
  includePriority,
  includeStatus,
  includeAddons,
  flowsData,
  includeFlows,
  shouldSeeDropdown,
  filter,
  updateSubFilter,
  updateFilter,
  accounts,
  packs,
  accountName,
  ignoreInCountAndHide,
  onAccountsSearchTermChange,
  accountsLoadMore,
  accountsHasMore,
  accountsLoading,
  packsLoading,
  addOnsData,
  accountsLabel,
}: SubFilterProps) => {
  const items = useMemo(() => {
    let returnVal: FilterItemBase<any>[] = [
      {
        type: FilterViewTypes.DateRange,
        label: "Dates",
        propertyName: "dates",
        items: [],
        placeholder: "",
      },
    ];
    if (includeAccounts) {
      returnVal.push({
        type: FilterViewTypes.Checkbox,
        label: "Accounts",
        propertyName: "showCustomers",
        items: [],
        placeholder: "",
      });
    }
    if (includeAccountsDropdown) {
      returnVal.push({
        type: FilterViewTypes.MultiSelectWithWindow,
        label: accountsLabel || "Accounts",
        propertyName: "accounts",
        className: "accountsFilter",
        items: accounts,
        placeholder: "All Customer Accounts",
        otherProps: {
          initialCheckedItems: [allCustomerAccounts],
          searchPredicate: (item: Accounts, term: string) => item.text.toLowerCase().includes(term.toLowerCase()),
          getLabel: (item: Accounts) => item.text,
          // Temporary
          getDescription: (item: Accounts) => item?.text || "",
          remoteSearch: filter.type !== "sp",
          onRemoteSearchTermChange: onAccountsSearchTermChange,
          fetchItems: accountsLoadMore,
          hasMore: accountsHasMore,
          isLoading: accountsLoading,
        },
      });

      returnVal.push({
        type: FilterViewTypes.Checkbox,
        label: "Include My Account Data",
        className: "includeMyData",
        propertyName: "includeMyData",
        items: [],
        placeholder: "",
        otherProps: {
          noToggle: true,
        },
      });
    }

    if (includePacks) {
      returnVal.push({
        type: FilterViewTypes.MultiSelectWithWindow,
        label: "Packs",
        propertyName: "packId",
        className: "packsFilter",
        items: packs,
        placeholder: "All Packs",
        otherProps: {
          initialCheckedItems: [],
          searchPredicate: (item: Accounts, term: string) => item.text.toLowerCase().includes(term.toLowerCase()),
          getLabel: (item: Accounts) => item.text,
          getDescription: (item: Accounts) => item?.text || "",
          isLoading: packsLoading,
        },
      });

      returnVal.push({
        type: FilterViewTypes.Select,
        label: "Status",
        propertyName: "packStatusId",
        items: [
          { text: "All", value: PackStatusFilter.All },
          { text: "Active", value: PackStatusFilter.Active },
          { text: "Expired", value: PackStatusFilter.Expired },
        ],
        placeholder: "",
        otherProps: {
          defaultSelected: PackStatusFilter.All,
        },
      });

      returnVal.push({
        type: FilterViewTypes.Select,
        label: "Type",
        propertyName: "packTypeId",
        items: [
          { text: "All", value: PackTypeId.All },
          { text: "Free", value: PackTypeId.Free },
          { text: "Other", value: PackTypeId.Other },
          { text: "Paid", value: PackTypeId.Paid },
          { text: "Trial", value: PackTypeId.Trial },
        ],
        placeholder: "",
        otherProps: {
          defaultSelected: PackTypeId.All,
        },
      });
    }

    if (includeAccountsDropdown && shouldSeeDropdown) {
      returnVal.push({
        type: FilterViewTypes.Select,
        label: "Publisher",
        propertyName: "type",
        items: [
          { text: `${accountName || "My Account"}`, value: "sp" },
          { text: "All Publishers", value: "account" },
        ],
        placeholder: "",
        otherProps: {
          defaultSelected: filter.type || "sp",
          className: "publisher-dropdown",
        },
      });
    }

    if (includeVideoType) {
      const options = [
        { value: "All", text: "All" },
        { value: "EmbeddedVideo", text: "Embedded" },
        { value: "Video", text: "Uploaded" },
      ];
      returnVal.push({
        type: FilterViewTypes.Select,
        label: "Type",
        className: "videoTypeFilter",
        propertyName: "videoType",
        items: options,
        placeholder: "",
        otherProps: {
          defaultSelected: "All",
        },
      });
    }

    if (includeDistinct) {
      returnVal.push({
        type: FilterViewTypes.Select,
        // non-strings are accepted here for "select" components
        // @ts-ignore
        label: (
          <div className="activity-filter-container">
            Activity
            <Tooltip
              target={<Icon className="info circle" />}
              hideOnScroll
              showAlways
              content={`Select “Total” to include all user activities, even repeats. Choose “Distinct” to exclude repeated activities by the same user.`}
              position="top center"
              hoverable
            />
          </div>
        ),
        propertyName: "distinct",
        className: "distinctFilter",
        items: [
          { text: "Total", value: "Total" },
          { text: "Distinct", value: "Distinct" },
        ],
        placeholder: "",
        otherProps: {
          defaultSelected: "Total",
        },
      });
    }
    if (includePriority) {
      returnVal.push({
        type: FilterViewTypes.MultiSelect,
        label: "Priority",
        propertyName: "priority",
        placeholder: "All Priority Levels",
        items: mappedPriorityLevels,
        otherProps: {
          search: customDropdownSearch,
          className: "priorityLevels",
        },
      });
    }
    if (includeStatus) {
      returnVal.push({
        type: FilterViewTypes.MultiSelect,
        label: "Status",
        propertyName: "status",
        placeholder: "All Statuses",
        items: mappedStatusLevels,
        otherProps: {
          search: customDropdownSearch,
          className: "statusFilter",
        },
      });
    }

    if (includeAddons && addOnsData?.length) {
      returnVal.push({
        type: FilterViewTypes.MultiSelect,
        label: "Add-On",
        propertyName: "addOnId",
        placeholder: "All Add-Ons",
        items: addOnsData.map((val) => ({ text: val.name, value: val.id })),
        otherProps: {
          search: customDropdownSearch,
        },
      });

      returnVal.push({
        type: FilterViewTypes.Select,
        label: "Status",
        propertyName: "isActive",
        items: [
          { text: "All", value: 0 },
          { text: "Active", value: 1 },
          { text: "Expired", value: 2 },
        ],
        otherProps: {
          defaultSelected: 0,
        },
      });
    }

    const flows = flowsData ?? [];
    if (includeFlows && flows.length) {
      returnVal.push({
        type: FilterViewTypes.Select,
        label: "Flow",
        propertyName: "flowId",
        className: "flowsFilter",
        items: [placeholderFlowId, ...flows.map((flow) => ({ text: flow.title, value: flow.id }))],
        placeholder: "",
        otherProps: {
          defaultSelected: placeholderFlowId.value,
        },
      });
    }

    const removeHiddenFilterOptions = returnVal.filter(
      (filter) => !ignoreInCountAndHide?.includes(filter.propertyName),
    );
    return removeHiddenFilterOptions;
  }, [
    includeAccounts,
    includeAccountsDropdown,
    includePacks,
    includeVideoType,
    includeDistinct,
    includeAddons,
    includeFlows,
    flowsData,
    accounts,
    packs,
    accountName,
    filter.type,
    ignoreInCountAndHide,
    shouldSeeDropdown,
    includePriority,
    includeStatus,
    onAccountsSearchTermChange,
    accountsLoadMore,
    accountsHasMore,
    accountsLoading,
    packsLoading,
    addOnsData,
    accountsLabel,
  ]);

  const handleUpdate = useCallback(
    (newFilter: any) => {
      // If the type (publisher) changed, reset packId
      if (includePacks && newFilter.type !== filter.type) {
        newFilter = { ...newFilter, packId: [] };
      }

      updateSubFilter(newFilter);
      updateFilter(newFilter);
    },
    [updateFilter, updateSubFilter, includePacks, filter.type],
  );
  return (
    <FilterFormBuilder
      items={items}
      filterOptions={{}}
      filter={filter}
      // Ignoring because GenericFiltersMap does not definitively
      // assert that the values we expect are present
      // @ts-ignore
      updateFilter={handleUpdate}
    />
  );
};
